import type { RoundedSystemMixEntry } from '@/utils/dashboardFases';
import { useSystemDot } from '@/stores/systems';


interface DashboardSysteemBreakdownProps {
  /**
   * Print-ready project-wide systeem lines — whole m², already apportioned so the
   * column sums to the tile above (see utils/dashboardFases.roundSystemMix). This
   * component only prints; it must not round, or the two would drift apart again.
   */
  lines: RoundedSystemMixEntry[];
  /** Collector fetch has not resolved yet — show a marker, never "geen collectoren". */
  pending?: boolean;
  /**
   * The collector list is missing or known-stale — the SAME flag the tile above
   * uses for its "—" state, passed in rather than re-derived, so the tile and
   * these lines can never disagree about whether the data can be trusted.
   */
  unavailable?: boolean;
  /** Why it is unavailable, for the title/tooltip. Never decides the state itself. */
  error?: string | null;
}

/**
 * Per-systeem "uitgevoerd / totaal m²" printed under the projectkop's
 * "Uitgevoerd m²" tile, so the headline number is explained where it is read.
 *
 * ALWAYS printed — never a hover tooltip: the dashboard is opened on tablets
 * where hover does not exist (Igor, 2026-08).
 *
 * Consistency BY CONSTRUCTION, on three levels: the caller derives the tile's
 * number and these lines from ONE faseAggregate() over the same collector list
 * (faseAggregate guarantees Σ systemMix[].uitgevoerdM2 === uitgevoerdM2); the
 * caller apportions the rounding so the PRINTED lines sum to the PRINTED tile;
 * and `unavailable` is the caller's own tile flag, so "we cannot say" is never
 * shown here while the tile still prints a confident number.
 *
 * The tile deliberately does NOT read Odoo's stored x_uitgevoerd_totaal_aantal:
 * that compute was never repointed to the collector basis ADR-028 decided, so it
 * disagrees with the collectoren on most projects (see the ADR-028 amendment in
 * docs/decisions.md).
 */
export function DashboardSysteemBreakdown({
  lines,
  pending = false,
  unavailable = false,
  error = null,
}: DashboardSysteemBreakdownProps) {
  // Colour dots come from the system roster (Odoo-defined), so a new system gets a
  // dot without touching this component.
  const systemDot = useSystemDot();
  return (
    <div className="mt-2 border-t border-gray-200 pt-2" aria-label="Uitgevoerd per systeem">
      {pending ? (
        <p className="text-[11px] text-td-light" aria-label="Systeemverdeling laden">
          …
        </p>
      ) : unavailable ? (
        <p className="text-[11px] text-td-light" title={error ?? undefined}>Systeemverdeling niet beschikbaar</p>
      ) : lines.length === 0 ? (
        <p className="text-[11px] text-td-muted">Geen collectoren in dit project</p>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {lines.map((s) => (
            <span
              key={s.systeem}
              className="inline-flex max-w-full items-center gap-1.5 text-[11px] font-bold text-td-navy"
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${systemDot(s.systeem)}`} aria-hidden="true" />
              <span className="truncate">{s.systeem}</span>
              <span className="shrink-0 font-normal text-td-muted">
                {`· ${s.uitgevoerdM2} / ${s.m2} m²`}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
